import React, { useEffect, useRef } from 'react';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

function NovaOrb30() {
  return (
    <div
      style={{
        width: 30,
        height: 30,
        minWidth: 30,
        borderRadius: '50%',
        background: '#fffcf9',
        boxShadow: '0 0 0 1.5px #d4537e, 0 0 0 3px #fffcf9',
        fontFamily: '"Playfair Display", Georgia, serif',
        fontStyle: 'italic',
        fontWeight: 900,
        fontSize: 12,
        color: '#993556',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      N
    </div>
  );
}

function InitialsAvatar({ children, bg, color }) {
  return (
    <div
      style={{
        width: 30,
        height: 30,
        minWidth: 30,
        borderRadius: '50%',
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontFamily: FONT_BODY,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Slide-out notifications panel for demo nav (fertility + TTC).
 * @param {{ open: boolean; onClose: () => void; bellRef: React.RefObject<HTMLElement> }} props
 */
export default function DemoNotificationsPanel({ open, onClose, bellRef }) {
  const panelRef = useRef(null);
  const [tab, setTab] = React.useState('all');

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (panelRef.current?.contains(t)) return;
      if (bellRef?.current?.contains(t)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, onClose, bellRef]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Notifications"
      style={{
        position: 'absolute',
        left: '100%',
        bottom: 60,
        width: 320,
        marginLeft: 0,
        background: 'var(--white, #fffcf9)',
        border: '0.5px solid var(--border, #e8e0d8)',
        borderRadius: 12,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        zIndex: 200,
        overflow: 'hidden',
        fontFamily: FONT_BODY,
      }}
    >
      <div
        style={{
          padding: '12px 14px',
          borderBottom: '0.5px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Notifications</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--pink-d)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: FONT_BODY,
              padding: 0,
            }}
          >
            Mark all read
          </button>
          <button
            type="button"
            aria-label="Close notifications"
            onClick={onClose}
            style={{
              fontSize: 14,
              color: 'var(--muted)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', borderBottom: '0.5px solid var(--border)', padding: '0 14px' }}>
        {[
          { id: 'all', label: 'All', count: 3 },
          { id: 'events', label: 'Events' },
          { id: 'nova', label: 'Nova' },
          { id: 'community', label: 'Community' },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: tab === t.id ? 'var(--pink-d)' : 'var(--muted)',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid var(--pink)' : '2px solid transparent',
              padding: '7px 12px',
              marginBottom: -1,
              background: 'none',
              cursor: 'pointer',
              fontFamily: FONT_BODY,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {t.label}
            {t.id === 'all' && t.count != null ? (
              <span
                style={{
                  minWidth: 16,
                  height: 16,
                  padding: '0 4px',
                  borderRadius: 8,
                  fontSize: 9,
                  fontWeight: 700,
                  background: 'var(--pink)',
                  color: 'white',
                  marginLeft: 4,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {t.count}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {/* Item 1 */}
        <button
          type="button"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '11px 14px',
            border: 'none',
            borderBottom: '0.5px solid var(--sand-2, #ede8df)',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
            background: 'var(--pink-l, #fbeaf0)',
            fontFamily: FONT_BODY,
          }}
        >
          <NovaOrb30 />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Nova · Today for you</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>
              Your monitoring scan is tomorrow. I&apos;ve prepared your question list for Dr. Jain.
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Today · 7:02am</div>
          </div>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--pink)',
              flexShrink: 0,
              marginTop: 4,
            }}
            aria-hidden
          />
        </button>

        {/* Item 2 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '11px 14px',
            borderBottom: '0.5px solid var(--sand-2, #ede8df)',
            background: 'var(--pink-l, #fbeaf0)',
            cursor: 'pointer',
          }}
        >
          <InitialsAvatar bg="var(--purple-l, #eeedfe)" color="var(--purple-d, #534ab7)">
            JM
          </InitialsAvatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Event invite from Jamie M.</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>
              IVF Coffee Circle — Columbus · Sat Apr 12 · 10:00am
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button
                type="button"
                style={{
                  background: 'var(--pink)',
                  color: 'white',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                }}
              >
                Going
              </button>
              <button
                type="button"
                style={{
                  background: 'var(--sand-2)',
                  color: 'var(--muted)',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                }}
              >
                Maybe
              </button>
              <button
                type="button"
                style={{
                  background: 'var(--sand-2)',
                  color: 'var(--muted)',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 6,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: FONT_BODY,
                }}
              >
                Decline
              </button>
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 6 }}>Yesterday · 3:14pm</div>
          </div>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--pink)',
              flexShrink: 0,
              marginTop: 4,
            }}
            aria-hidden
          />
        </div>

        {/* Item 3 */}
        <button
          type="button"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '11px 14px',
            border: 'none',
            borderBottom: '0.5px solid var(--sand-2, #ede8df)',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
            background: 'var(--pink-l, #fbeaf0)',
            fontFamily: FONT_BODY,
          }}
        >
          <InitialsAvatar bg="var(--sage-l, #e1f5ee)" color="var(--sage-d, #085041)">
            NR
          </InitialsAvatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Noelle R. replied to your post</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>
              That happened to me too, my cycle was cancelled...
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Yesterday · 11:40am</div>
          </div>
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--pink)',
              flexShrink: 0,
              marginTop: 4,
            }}
            aria-hidden
          />
        </button>

        {/* Item 4 */}
        <button
          type="button"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '11px 14px',
            border: 'none',
            borderBottom: '0.5px solid var(--sand-2, #ede8df)',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
            background: 'var(--white)',
            fontFamily: FONT_BODY,
          }}
        >
          <NovaOrb30 />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Nova · Reminder</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>
              Gonal-F 150IU injection tonight at 9pm. Don&apos;t forget to rotate sites.
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>Yesterday · 8:45pm</div>
          </div>
        </button>

        {/* Item 5 */}
        <button
          type="button"
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            padding: '11px 14px',
            border: 'none',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
            background: 'var(--white)',
            fontFamily: FONT_BODY,
          }}
        >
          <InitialsAvatar bg="var(--pink-l)" color="var(--pink-d)">
            SK
          </InitialsAvatar>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>IVF Support Circle tomorrow</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.4, marginTop: 2 }}>
              Reminder · Dublin Rec Center · 10:00am · 12 going
            </div>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 2 }}>2 days ago</div>
          </div>
        </button>
      </div>

      <div
        style={{
          padding: '10px 14px',
          textAlign: 'center',
          borderTop: '0.5px solid var(--sand-2)',
        }}
      >
        <button
          type="button"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--pink-d)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontFamily: FONT_BODY,
          }}
        >
          See all notifications →
        </button>
      </div>
    </div>
  );
}
